<template lang="">
  <l-icon v-on="$listeners">
    <div
      class="marker-pin d-flex justify-content-center align-items-center"
      :class="pinClasses"
      :style="pinStyle"
    >
      <div class="flex-none marker-pin-child">
        <slot><i class="fa fa-circle fa-xs"></i></slot>
      </div>
    </div>
  </l-icon>
</template>
<script>
import { LIcon } from "vue2-leaflet";
import { getContrastColorFromRgba } from "../../../utils/contrastColor";

export default {
  components: {
    LIcon,
  },
  props: {
    /**
     * Marker type for predefined styles: 'active', 'primary', 'secondary', 'tertiary', or 'none'
     * Takes precedence over color prop
     */
    type: {
      type: String,
      default: null,
      validator: (value) => {
        return ["active", "primary", "secondary", "tertiary", "warning", "none"].includes(
          value
        );
      },
    },
    /**
     * Custom color for the marker pin background
     * Can be any valid CSS color (hex, rgb, rgba, named color)
     * Only used if type prop is not set
     */
    color: {
      type: String,
      default: null,
    },
    backgroundColor: {
      type: String,
      default: null,
    },
  },
  computed: {
    pinClasses() {
      // Return type class if provided
      if (this.type) {
        return this.type;
      }
      // Fallback to any classes passed from parent
      return [
        this.$vnode.data.staticClass,
        this.$vnode.data.class,
        this.$attrs.class,
      ];
    },
    pinStyle() {
      // If type is set, don't apply custom colors (use CSS classes instead)
      if (this.type) {
        return {};
      }
      // If no color provided, return empty object to use CSS defaults
      if (!this.color) {
        return {};
      }
      // if color and background color are provided, use them
      if (this.backgroundColor && this.color) {
        return {
          "--pin-bg-color": this.backgroundColor,
          "--pin-border-color": this.color,
          "--pin-text-color": this.color,
          "--pin-text-shadow":
            getContrastColorFromRgba(this.backgroundColor) === "white"
              ? "0 0 2px rgba(0, 0, 0, 0.8)"
              : "0 0 2px rgba(255, 255, 255, 0.8)",
        };
      }
      // If only backgroun color is provided, use it with contrast text color
      const textColor = getContrastColorFromRgba(this.color);

      // Text shadow should be opposite of text color for contrast
      const textShadow =
        textColor === "white"
          ? "0 0 2px rgba(0, 0, 0, 0.8)"
          : "0 0 2px rgba(255, 255, 255, 0.8)";

      return {
        "--pin-bg-color": this.backgroundColor,
        "--pin-text-color": textColor,
        "--pin-border-color": textColor,
        "--pin-text-shadow": textShadow,
      };
    },
  },
  methods: {},
  data() {
    return {};
  },
  mounted() {},
};
</script>
<style lang="scss" scoped>
$marker-size: 1.5;
$font-size-base: 18px;

.marker-pin {
  background: var(--pin-bg-color, white);
  width: $marker-size * $font-size-base;
  height: $marker-size * $font-size-base;
  display: block;
  left: -0.4 * $font-size-base;
  top: -1.4 * $font-size-base;
  position: relative;
  border-radius: $marker-size * $font-size-base $marker-size * $font-size-base 0;
  transform: rotate(45deg);
  border: 3 * $font-size-base/16 solid var(--pin-border-color, #c2c2c2);
  transition: border-width 0.2s, box-shadow 0.2s;
  cursor: pointer;
  pointer-events: auto;
}
.marker-pin:hover {
  border-width: 4 * $font-size-base/16;
  box-shadow: 0 0 8px rgba(0, 0, 0, 0.6);
}
.marker-pin-child {
  transform: rotate(-45deg);
  color: var(--pin-text-color, black);
  text-shadow: var(--pin-text-shadow, 0 0 2px rgba(255, 255, 255, 0.8));
}
.none {
  border: 3 * $font-size-base/16 solid rgba(70, 70, 70, 1) !important;
  background-color: rgba(194, 194, 194, 1) !important;
}
.none .marker-pin-child {
  color: black !important;
}
.active {
  border: 3 * $font-size-base/16 solid rgba(255, 255, 255, 1) !important;
  background-color: rgba(136, 152, 63, 1) !important;
}
.active .marker-pin-child {
  color: black !important;
}
.primary {
  border: 3 * $font-size-base/16 solid rgba(0, 200, 248, 1) !important;
  background-color: white !important;
}
.secondary {
  border: 3 * $font-size-base/16 solid rgba(108, 117, 125, 1) !important;
  background-color: white !important;
}
.tertiary {
  border: 3 * $font-size-base/16 solid rgba(135, 152, 63, 1) !important;
  background-color: white !important;
}
.warning {
  border: 3 * $font-size-base/16 solid rgba(255, 193, 7, 1) !important;
  background-color: white !important;
}
</style>
